import { get } from "@rails/request.js";
import { escapeHTML } from "./gl_modal_shell";
import { statusBadgeClass } from "./status_badge";

// State Lead Flow (#15927) on the realtime order page: every state ranked by
// its share of the picked lead type + quality over the last 30 days. Owner,
// 2026-10-06: "Not give lead numbers" — the endpoint sends shares only, and
// nothing here derives a count.
//
// One renderer for both hosts the order form has: the standalone page's modal
// and the GOAT Store dialog's in-section panel (a dialog may carry no nested
// modal, #13900 §6). The host element is owned by mountStateLeadFlow: it paints
// the body, handles the quality toggle, and drops a response that lands after
// a newer request or destroy().

const QUALITY_LABELS = { standard: "Standard", premium: "Premium", blended: "Blended" };
const LOAD_ERROR = "State lead flow could not be loaded. Please try again.";
// Shares under this (in percent) read "<0.1%" rather than a misleading 0.0%.
const MIN_SHOWN_PERCENT = 0.1;

// Everything renders inside one fixed-height .rlo-flow box — the ranking, the
// loading line and the error alike — so the modal keeps one size whatever it
// is showing (owner, 2026-10-06). Only the list scrolls.
function statusHTML(message) {
  return `<div class="rlo-flow"><p class="gl-form-hint rlo-flow__status">${message}</p></div>`;
}

export function qualityLabel(quality) {
  return QUALITY_LABELS[quality] || quality;
}

export function formatShare(share) {
  const percent = Number(share) * 100;
  if (!percent) return "0%";
  if (percent < MIN_SHOWN_PERCENT) return `<${MIN_SHOWN_PERCENT}%`;
  return `${percent.toFixed(1)}%`;
}

function toggleHTML(qualities, current) {
  if (qualities.length < 2) return "";
  const chips = qualities.map((quality) => {
    const on = quality === current;
    return `<button type="button" class="rlo-chip rlo-chip--sm${on ? " is-on" : ""}" ` +
      `data-flow-quality="${escapeHTML(quality)}" aria-pressed="${on}">${escapeHTML(qualityLabel(quality))}</button>`;
  });
  return `<div class="rlo-chips rlo-flow__qualities" role="group" aria-label="Lead quality">${chips.join("")}</div>`;
}

const SELECTED_BADGE_HTML = `<span class="${statusBadgeClass("success")}" data-flow-selected>Selected</span>`;

function rowHTML(row, index, selected) {
  const pill = selected.has(row.state) ? ` ${SELECTED_BADGE_HTML}` : "";
  return `<tr data-flow-state="${escapeHTML(row.state)}"><td class="rlo-flow__rank">${index + 1}</td>` +
    `<td>${escapeHTML(row.name)}${pill}</td>` +
    `<td class="text-end">${formatShare(row.share)}</td></tr>`;
}

export function stateLeadFlowHTML({ data, qualities, selectedStates = [] }) {
  const selected = new Set(selectedStates);
  const caption = `Each state's share of ${escapeHTML(data.lead_type)} ` +
    `${escapeHTML(qualityLabel(data.quality))} leads over the last ${Number(data.lookback_days)} days, busiest first.`;
  return '<div class="rlo-flow">' +
    `${toggleHTML(qualities, data.quality)}` +
    `<p class="gl-form-hint rlo-flow__caption">${caption}</p>` +
    '<div class="rlo-flow__list">' +
    '<table class="table gl-table-enhanced gl-table-enhanced--panel mb-0">' +
    '<thead><tr><th scope="col">Rank</th><th scope="col">State</th><th scope="col" class="text-end">Share</th></tr></thead>' +
    `<tbody>${data.states.map((row, index) => rowHTML(row, index, selected)).join("")}</tbody>` +
    "</table></div></div>";
}

export async function fetchStateLeadFlow(url, leadClass, quality) {
  const response = await get(url, { query: { lead_class: leadClass, quality }, responseKind: "json" });
  if (!response.ok) throw new Error(`state_lead_flow ${response.statusCode}`);
  return response.json;
}

// Marks each painted row Selected or not, in place — a repaint would drop the
// list's scroll position on every chip click (#16096).
function paintSelected(host, selected) {
  host.querySelectorAll("tr[data-flow-state]").forEach((tr) => {
    const cell = tr.cells[1];
    const badge = cell.querySelector("[data-flow-selected]");
    const on = selected.has(tr.dataset.flowState);
    if (on && !badge) cell.insertAdjacentHTML("beforeend", ` ${SELECTED_BADGE_HTML}`);
    if (!on && badge) {
      badge.remove();
      // The name and the badge's leading space share one text node.
      const name = cell.lastChild;
      if (name?.nodeType === Node.TEXT_NODE) name.textContent = name.textContent.trimEnd();
    }
  });
}

// Paints the ranking into `host` and keeps it current as the agent toggles
// quality or changes the order's target states (setSelectedStates).
// Returns { setSelectedStates, destroy } — call destroy when the host closes.
export function mountStateLeadFlow(host, { url, leadClass, quality, qualities, selectedStates }) {
  let token = 0;

  const load = async (nextQuality) => {
    const mine = (token += 1);
    host.setAttribute("aria-busy", "true");
    host.innerHTML = statusHTML("Loading…");
    try {
      const data = await fetchStateLeadFlow(url, leadClass, nextQuality);
      if (mine !== token) return;
      host.innerHTML = stateLeadFlowHTML({ data, qualities, selectedStates });
    } catch {
      if (mine !== token) return;
      host.innerHTML = statusHTML(LOAD_ERROR);
    } finally {
      if (mine === token) host.removeAttribute("aria-busy");
    }
  };

  const onClick = (event) => {
    const chip = event.target.closest("[data-flow-quality]");
    if (!chip || chip.classList.contains("is-on")) return;
    load(chip.dataset.flowQuality);
  };

  host.addEventListener("click", onClick);
  load(quality);

  return {
    // #16096: the badges follow the state chips while the list is open. A
    // ranking still loading picks the new set up when it paints.
    setSelectedStates(states) {
      selectedStates = states;
      paintSelected(host, new Set(states));
    },
    destroy() {
      token += 1;
      host.removeEventListener("click", onClick);
    },
  };
}
